import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideMessage } from './actions';
import { clearSuccess } from './actions/success';
import { Box, CenteredSpinner, CustomPortal } from './commonComponents';
import { ErrorShower } from './commonComponents/ErrorShower';
import { BestInDentalNomineePopup } from './commonComponents/popups/BestInDentalNomineePopup';
import { BestInDentalWinnerPopup } from './commonComponents/popups/BestInDentalWinnerPopup';
import { UnavailableMobile } from './commonComponents/UnavailableMobile';
import { AcceptTermsPopup } from './components/AcceptTermsPopup';
import { BidApplicationPopup } from './components/BestInDental/BidApplicationPopup';
import { JobsInviteConfirmationPopup } from './components/JobsInviteConfirmationPopup';
import { JobsInviteSelectionPopup } from './components/JobsInviteSelectionPopup';
import { ModalQueueProvider } from './components/ModalQueue';
import { OfficeTimePerPatientPopup } from './components/OfficeTimePerPatientPopup/OfficeTimePerPatientPopup';
import { OfficeTutorialPopupProvider } from './components/OfficeTutorialPopup';
import ParkingInfoPopup from './components/ParkingInfoPopup';
import { ReferralRewardPopup } from './components/ReferralRewardPopup';
import { SuccessPopup as SuccessWorkerClassificationPopup } from './components/WorkerClassification/components/SuccessPopup';
import { BestInDentalV2 } from './constants/ff.posthog';
import { growthbook } from './growthbook';
import {
  useCookieYes,
  useInitialLoading,
  usePermPosthogSessionRecording,
  useShiftRequestPosthogSessionRecording,
  useSignUpPosthogSessionRecording,
  useUserSelector,
} from './hooks';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { AuthenticatedRoutes } from './routes/AuthenticatedRoutes';
import { UnauthenticatedRoutes } from './routes/UnauthenticatedRoutes';
import { useReferralHandler } from './hooks/useReferral';
import {
  LegacyReferralLinkToast,
  useLegacyReferralLinkNotice,
} from './components/LegacyReferralLinkToast';
import { useCampaignTracker } from './hooks/useCampaignTracker';

export default function App() {
  const dispatch = useDispatch();
  const { isLoading } = useInitialLoading();
  const userLogged = useSelector((state) => state.user.user);
  const selectedChildOffice = useUserSelector();
  const message = useSelector((state) => state.message.message);
  const isMobileTablet = window.innerWidth <= 760;

  const isBidV2 = useFeatureFlag(BestInDentalV2);

  useReferralHandler();
  const legacyReferralLinkNotice = useLegacyReferralLinkNotice();
  useCampaignTracker();

  usePermPosthogSessionRecording();
  useSignUpPosthogSessionRecording();
  useShiftRequestPosthogSessionRecording();

  useCookieYes();

  useEffect(() => {
    if (selectedChildOffice) {
      growthbook.setAttributes({
        id: selectedChildOffice?.id,
        userType: selectedChildOffice?.user_type,
        ...(selectedChildOffice?.address &&
          selectedChildOffice?.address[0] && { state: selectedChildOffice.address[0].state }),
      });
    }
  }, [selectedChildOffice]);

  const {
    text,
    title,
    route,
    params,
    isSuccess,
    isError,
    popupProps,
    action,
    buttonText,
    modalType,
  } = message;

  if (isMobileTablet) {
    return <UnavailableMobile />;
  }

  if (isLoading) {
    return <CenteredSpinner size={32} />;
  }

  if (!userLogged || !userLogged?.is_profile_complete || userLogged?.user_type !== 'DNT') {
    return (
      <Box>
        <UnauthenticatedRoutes />
        <ErrorShower />
        {text || title ? (
          <CustomPortal
            isSuccess={isSuccess}
            isError={isError}
            popupProps={popupProps}
            title={title}
            text={text}
            route={route}
            params={params}
            buttonText={buttonText}
            submitPressed={() => dispatch(hideMessage())}
          />
        ) : null}
        <LegacyReferralLinkToast
          open={legacyReferralLinkNotice.isOpen}
          onClose={legacyReferralLinkNotice.close}
        />
        <SuccessWorkerClassificationPopup />
      </Box>
    );
  }

  return (
    <ModalQueueProvider>
      <OfficeTutorialPopupProvider>
        <AuthenticatedRoutes />
        {text || title ? (
          <CustomPortal
            isSuccess={isSuccess}
            isError={isError}
            popupProps={popupProps}
            title={title}
            text={text}
            route={route}
            params={params}
            buttonText={buttonText}
            dataCy={modalType === 'feedbackSuccess' ? 'feedback-success-modal' : undefined}
            rightButtonDataCy={modalType === 'feedbackSuccess' ? 'feedback-success-ok-button' : undefined}
            submitPressed={() => {
              if (action) {
                dispatch(clearSuccess(action));
                return;
              }
              dispatch(hideMessage());
            }}
          />
        ) : null}
        <ParkingInfoPopup />
        <AcceptTermsPopup />
        <LegacyReferralLinkToast
          open={legacyReferralLinkNotice.isOpen}
          onClose={legacyReferralLinkNotice.close}
        />
        <SuccessWorkerClassificationPopup />
        <OfficeTimePerPatientPopup />
        <ReferralRewardPopup />
        <JobsInviteConfirmationPopup />
        <JobsInviteSelectionPopup />
        {!isBidV2 && <BestInDentalNomineePopup />}
        {!isBidV2 && <BestInDentalWinnerPopup />}
        {isBidV2 && <BidApplicationPopup />}
        <ErrorShower />
      </OfficeTutorialPopupProvider>
    </ModalQueueProvider>
  );
}
